// Signal-backed store for task lists and personal tasks.
// Follows the lazy-load / refresh pattern established in favorites.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type {
  TaskList,
  ListStatus,
  StatusCategory,
  PersonalTask,
  PersonalTaskStep,
  PersonalTaskRecurrence,
  RecurrenceRule,
  PersonalTaskMergeResult,
} from "@juuplan/shared";
import { api } from "../lib/api";

// ---------- local composite types ----------

export interface PersonalTaskDetail extends PersonalTask {
  steps: PersonalTaskStep[];
  recurrence: PersonalTaskRecurrence | null;
}

// ---------- space members ----------

export interface SpaceMember {
  user_id: number;
  role: string;
  name: string;
  email: string;
  avatar_url: string | null;
}

export async function fetchSpaceMembers(spaceId: number): Promise<SpaceMember[]> {
  const data = await api<SpaceMember[]>(`/api/spaces/${spaceId}/members`, { silentError: true });
  return data ?? [];
}

// ---------- lists ----------

export interface ListsState {
  lists: TaskList[];
  loading: boolean;
}

const listsSignal: Signal<ListsState> = signal({ lists: [], loading: true });
let listsStarted = false;

async function loadLists(): Promise<void> {
  try {
    const data = await api<TaskList[]>("/api/lists", { silentError: true });
    listsSignal.value = { lists: data ?? [], loading: false };
  } catch {
    listsSignal.value = { ...listsSignal.value, loading: false };
  }
}

export function useLists(): ListsState {
  useEffect(() => {
    if (!listsStarted) {
      listsStarted = true;
      void loadLists();
    }
  }, []);
  return listsSignal.value;
}

export function refreshLists(): Promise<void> {
  return loadLists();
}

/** Non-reactive lookup of a loaded list (mirrors findUser in store/users). */
export function findList(id: number | null | undefined): TaskList | undefined {
  if (id === null || id === undefined) return undefined;
  return listsSignal.value.lists.find((l) => l.id === id);
}

export async function createList(
  name: string,
  color?: string,
  spaceId?: number
): Promise<TaskList> {
  const result = await api<TaskList>("/api/lists", {
    method: "POST",
    body: {
      name,
      ...(color !== undefined ? { color } : {}),
      ...(spaceId !== undefined ? { space_id: spaceId } : {}),
    },
  });
  await loadLists();
  return result;
}

export async function updateList(
  id: number,
  patch: { name?: string; color?: string | null }
): Promise<TaskList> {
  const result = await api<TaskList>(`/api/lists/${id}`, {
    method: "PATCH",
    body: patch,
  });
  await loadLists();
  return result;
}

export async function deleteList(id: number): Promise<void> {
  await api<void>(`/api/lists/${id}`, { method: "DELETE" });
  await loadLists();
}

// ---------- per-list statuses ----------

const listStatusesSignal: Signal<Record<number, ListStatus[]>> = signal({});
const listStatusesLoading = new Set<number>();

async function loadListStatuses(listId: number): Promise<void> {
  try {
    const data = await api<ListStatus[]>(`/api/lists/${listId}/statuses`, {
      silentError: true,
    });
    listStatusesSignal.value = {
      ...listStatusesSignal.value,
      [listId]: data ?? [],
    };
  } catch {
    // leave prior value in place
  }
}

/**
 * Ordered status set for a list (reactive). Lazy-loads on first use per list.
 * Returns [] for the inbox (listId null) and while loading.
 */
export function useListStatuses(listId: number | null): ListStatus[] {
  useEffect(() => {
    if (listId != null && !listStatusesLoading.has(listId)) {
      listStatusesLoading.add(listId);
      void loadListStatuses(listId);
    }
  }, [listId]);
  if (listId == null) return [];
  return listStatusesSignal.value[listId] ?? [];
}

/** Synchronous read of any already-loaded list status set (non-reactive helper). */
export function getLoadedListStatuses(listId: number | null): ListStatus[] {
  if (listId == null) return [];
  return listStatusesSignal.value[listId] ?? [];
}

/** Reactive read of the full {listId -> statuses} map. */
export function useListStatusesMap(): Record<number, ListStatus[]> {
  return listStatusesSignal.value;
}

/** Trigger a one-time lazy load of a list's statuses (no-op if already loading). */
export function ensureListStatuses(listId: number): void {
  if (!listStatusesLoading.has(listId)) {
    listStatusesLoading.add(listId);
    void loadListStatuses(listId);
  }
}

export function refreshListStatuses(listId: number): Promise<void> {
  return loadListStatuses(listId);
}

export async function createListStatus(
  listId: number,
  input: { name: string; category: StatusCategory; color?: string; sort_order?: number }
): Promise<ListStatus> {
  const result = await api<ListStatus>(`/api/lists/${listId}/statuses`, {
    method: "POST",
    body: input,
  });
  await loadListStatuses(listId);
  return result;
}

export async function updateListStatus(
  statusId: number,
  listId: number,
  patch: { name?: string; category?: StatusCategory; color?: string; sort_order?: number }
): Promise<ListStatus> {
  const result = await api<ListStatus>(`/api/list-statuses/${statusId}`, {
    method: "PATCH",
    body: patch,
  });
  await loadListStatuses(listId);
  return result;
}

export async function reorderListStatuses(
  listId: number,
  orderedIds: number[]
): Promise<void> {
  await api<void>(`/api/lists/${listId}/statuses/reorder`, {
    method: "POST",
    body: { orderedIds },
  });
  await loadListStatuses(listId);
}

export async function deleteListStatus(
  statusId: number,
  listId: number,
  reassignTo?: number
): Promise<void> {
  const q = reassignTo != null ? `?reassign_to=${reassignTo}` : "";
  await api<void>(`/api/list-statuses/${statusId}${q}`, { method: "DELETE" });
  await Promise.all([loadListStatuses(listId), loadPersonalTasks()]);
}

// ---------- personal tasks ----------

export interface PersonalTaskQuery {
  /** "all" omits the list_id param; eine Zahl filtert auf genau diese Liste.
   *  Das frühere `null` („Ohne Liste") ist weg — jede Aufgabe hat eine Liste. */
  listId: number | "all";
  includeDone: boolean;
}

export interface PersonalTasksState {
  tasks: PersonalTask[];
  loading: boolean;
}

const tasksSignal: Signal<PersonalTasksState> = signal({ tasks: [], loading: true });
const querySignal: Signal<PersonalTaskQuery> = signal({ listId: "all", includeDone: false });
let tasksStarted = false;

// Monotonic request token. loadPersonalTasks fires the request that matches the
// CURRENT query, but responses arrive out of order — e.g. the one-time mount
// load (query "all") racing the scope effect's load (a specific list). Without
// a guard the slower "all" response overwrites the scoped one, so a list view
// ends up showing every list's tasks (foreign statuses then fall into the
// "Sonstiges" bucket). Only the latest-issued load may write the signal.
let loadSeq = 0;

function buildTasksUrl(q: PersonalTaskQuery): string {
  const params = new URLSearchParams();
  if (q.listId !== "all") {
    params.set("list_id", String(q.listId));
  }
  params.set("include_done", String(q.includeDone));
  return `/api/personal-tasks?${params.toString()}`;
}

async function loadPersonalTasks(): Promise<void> {
  const seq = ++loadSeq;
  tasksSignal.value = { ...tasksSignal.value, loading: true };
  try {
    const url = buildTasksUrl(querySignal.value);
    const data = await api<PersonalTask[]>(url, { silentError: true });
    if (seq !== loadSeq) return; // a newer load superseded this response
    tasksSignal.value = { tasks: data ?? [], loading: false };
  } catch {
    if (seq !== loadSeq) return;
    tasksSignal.value = { ...tasksSignal.value, loading: false };
  }
}

export function usePersonalTasks(): PersonalTasksState {
  useEffect(() => {
    if (!tasksStarted) {
      tasksStarted = true;
      void loadPersonalTasks();
    }
  }, []);
  return tasksSignal.value;
}

/** Non-reactive read of the currently loaded personal tasks. */
export function getLoadedPersonalTasks(): PersonalTask[] {
  return tasksSignal.value.tasks;
}

export function setPersonalTaskQuery(q: PersonalTaskQuery): void {
  querySignal.value = q;
  void loadPersonalTasks();
}

export function refreshPersonalTasks(): Promise<void> {
  return loadPersonalTasks();
}

export async function createPersonalTask(input: {
  title: string;
  list_id?: number | null;
  notes?: string | null;
  due_date?: string | null;
  priority?: string;
  /** Zuständige, erste = primär (#854). Nur auf geteilten (Space-)Listen. */
  assignee_ids?: number[];
}): Promise<PersonalTask> {
  const result = await api<PersonalTask>("/api/personal-tasks", {
    method: "POST",
    body: input,
  });
  await loadPersonalTasks();
  return result;
}

export async function updatePersonalTask(
  id: number,
  patch: {
    title?: string;
    notes?: string | null;
    due_date?: string | null;
    priority?: string;
    list_id?: number | null;
    status_id?: number | null;
    is_done?: boolean;
    sort_order?: number;
    /** Ersetzt die Zuständigen komplett (#854); [] leert sie. */
    assignee_ids?: number[];
  }
): Promise<PersonalTask> {
  const result = await api<PersonalTask>(`/api/personal-tasks/${id}`, {
    method: "PATCH",
    body: patch,
  });
  await loadPersonalTasks();
  return result;
}

export async function togglePersonalTaskDone(id: number, isDone: boolean): Promise<PersonalTask> {
  return updatePersonalTask(id, { is_done: isDone });
}

export async function deletePersonalTask(id: number): Promise<void> {
  await api<void>(`/api/personal-tasks/${id}`, { method: "DELETE" });
  await loadPersonalTasks();
}

/**
 * Merge `sourceId` into `targetPersonalTaskId` (same kind only — server-side
 * rule). The source becomes a closed gravestone (`merged_into_personal_task_id`);
 * loadPersonalTasks() afterwards pulls both the frozen source and the target
 * (now carrying the moved content) back in.
 */
export async function mergePersonalTask(
  sourceId: number,
  targetPersonalTaskId: number
): Promise<PersonalTaskMergeResult> {
  const result = await api<PersonalTaskMergeResult>(`/api/personal-tasks/${sourceId}/merge`, {
    method: "POST",
    body: { target_personal_task_id: targetPersonalTaskId },
  });
  await loadPersonalTasks();
  return result;
}

/** One-shot fetch of a single task with its steps and recurrence. Not stored in the signal. */
export function fetchPersonalTaskDetail(id: number): Promise<PersonalTaskDetail> {
  return api<PersonalTaskDetail>(`/api/personal-tasks/${id}`);
}

// ---------- steps ----------

/** List the steps of a single personal task (used by inline row expansion). */
export function fetchSteps(taskId: number): Promise<PersonalTaskStep[]> {
  return api<PersonalTaskStep[]>(`/api/personal-tasks/${taskId}/steps`);
}

export async function addStep(
  taskId: number,
  title: string,
  dueDate?: string | null
): Promise<PersonalTaskStep> {
  return api<PersonalTaskStep>(`/api/personal-tasks/${taskId}/steps`, {
    method: "POST",
    body: dueDate !== undefined ? { title, due_date: dueDate } : { title },
  });
}

/**
 * Link an existing task (project or personal) as a REFERENCE step. Exactly one
 * of the two target ids must be set; the server reads title/status live from
 * the target.
 */
export async function addLinkedStep(
  taskId: number,
  target: { linked_task_id: number } | { linked_personal_task_id: number }
): Promise<PersonalTaskStep> {
  return api<PersonalTaskStep>(`/api/personal-tasks/${taskId}/steps`, {
    method: "POST",
    body: target,
  });
}

export async function updateStep(
  stepId: number,
  patch: { title?: string; is_done?: boolean; due_date?: string | null; sort_order?: number }
): Promise<PersonalTaskStep> {
  return api<PersonalTaskStep>(`/api/personal-task-steps/${stepId}`, {
    method: "PATCH",
    body: patch,
  });
}

/**
 * Schritt in eine eigenständige Aufgabe umwandeln: dieselbe Zeile, nur ohne
 * Elternteil — sie taucht danach als Aufgabe in derselben Liste auf und
 * verschwindet aus der Checkliste der Eltern-Aufgabe. Referenz-Schritte lehnt
 * der Server ab (sie zeigen bereits auf eine Aufgabe).
 */
export async function promoteStep(stepId: number): Promise<PersonalTask> {
  const result = await api<PersonalTask>(`/api/personal-task-steps/${stepId}/promote`, {
    method: "POST",
  });
  await loadPersonalTasks();
  return result;
}

export async function deleteStep(stepId: number): Promise<void> {
  return api<void>(`/api/personal-task-steps/${stepId}`, { method: "DELETE" });
}

/**
 * "Offene Punkte übernehmen" (#826): die offenen Schritte WANDERN an eine neue
 * Aufgabe (kein Kopieren, keine Verknüpfung), die Quelle wird geschlossen. Der
 * Server macht das atomar; `step_ids` weglassen heisst "alle offenen".
 */
export async function carryOverSteps(
  taskId: number,
  input: {
    title: string;
    due_date?: string | null;
    remind_at?: string | null;
    step_ids?: number[];
  }
): Promise<PersonalTask> {
  return api<PersonalTask>(`/api/personal-tasks/${taskId}/carry-over`, {
    method: "POST",
    body: input,
  });
}

// ---------- recurrence ----------

export async function setRecurrence(
  taskId: number,
  rule: RecurrenceRule,
  intervalCount?: number
): Promise<PersonalTaskRecurrence> {
  return api<PersonalTaskRecurrence>(`/api/personal-tasks/${taskId}/recurrence`, {
    method: "PUT",
    body: { rule, ...(intervalCount !== undefined ? { interval_count: intervalCount } : {}) },
  });
}

export async function clearRecurrence(taskId: number): Promise<void> {
  return api<void>(`/api/personal-tasks/${taskId}/recurrence`, { method: "DELETE" });
}
